import { apiPostAsUser, responseErrorMessage } from './api_v2';

const AUTHENTICATION_SESSION_TRANSPORT = 'authentication_session';
const DESKTOP_OAUTH_COMPLETE_EVENT = 'output:desktop-oauth-complete';

export function getDesktopBridge() {
  if (typeof window === 'undefined') return null;

  return typeof window.electron?.openExternal === 'function' ? window.electron : null;
}

export function supportsDesktopOauthSession(bridge = getDesktopBridge()) {
  return typeof bridge?.oauth?.authorize === 'function';
}

export async function getDesktopOauthCapabilities(bridge = getDesktopBridge()) {
  if (!supportsDesktopOauthSession(bridge)) {
    return { authenticationSession: false, httpsCallbacks: false };
  }

  // Desktop builds that predate capability negotiation only implemented the
  // HTTPS callback contract. Preserve that behavior while new builds report
  // the OS-specific AuthenticationServices capability explicitly.
  if (typeof bridge.oauth.getCapabilities !== 'function') {
    return { authenticationSession: true, httpsCallbacks: true };
  }

  const capabilities = await bridge.oauth.getCapabilities();
  return {
    authenticationSession: capabilities?.authenticationSession === true,
    httpsCallbacks: capabilities?.httpsCallbacks === true,
  };
}

export async function openDesktopOauth({ bridge = getDesktopBridge(), startUrl, callbackUrl, capabilities = null }) {
  if (!bridge) return false;

  const negotiatedCapabilities = capabilities || (await getDesktopOauthCapabilities(bridge));
  if (
    negotiatedCapabilities.authenticationSession &&
    negotiatedCapabilities.httpsCallbacks &&
    supportsDesktopOauthSession(bridge) &&
    callbackUrl
  ) {
    const completion = await bridge.oauth.authorize({ startUrl, callbackUrl });
    window.dispatchEvent(new CustomEvent(DESKTOP_OAUTH_COMPLETE_EVENT, { detail: completion }));
    return { handedOff: true, completed: true, succeeded: completion?.ok === true, completion };
  }

  await bridge.openExternal(startUrl);
  return { handedOff: true, completed: false, succeeded: null, completion: null };
}

export async function startDesktopOauth({ purpose, provider, workspaceId }) {
  const bridge = getDesktopBridge();
  if (!bridge) return false;

  const capabilities = await getDesktopOauthCapabilities(bridge);
  const body = { purpose, provider };
  if (workspaceId) body.workspace_id = workspaceId;
  if (capabilities.authenticationSession && capabilities.httpsCallbacks) body.completion_transport = AUTHENTICATION_SESSION_TRANSPORT;

  const response = await apiPostAsUser('/desktop/oauth/tickets', body);
  const data = await response.json();
  const startUrl = data.data?.start_url;
  const callbackUrl = data.data?.callback_url;

  if (!response.ok || data.ok !== true || !startUrl) {
    throw new Error(responseErrorMessage(data, 'Could not start OAuth.'));
  }

  return openDesktopOauth({ bridge, startUrl, callbackUrl, capabilities });
}
